import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon, BookOpenIcon, ZoomInIcon } from 'lucide-react';
interface FlipbookModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  pages: string[];
}
export function FlipbookModal({
  isOpen,
  onClose,
  title,
  pages
}: FlipbookModalProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [isFlipping, setIsFlipping] = useState(false);
  const [ripple, setRipple] = useState<{
    x: number;
    y: number;
    id: number;
  } | null>(null);
  useEffect(() => {
    if (isOpen) setCurrentPage(0);
  }, [isOpen]);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, currentPage]);
  const triggerRipple = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRipple({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      id: Date.now()
    });
    setTimeout(() => setRipple(null), 600);
  };
  const goNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) triggerRipple(e);
    if (currentPage < pages.length - 1 && !isFlipping) {
      setDirection('next');
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentPage(p => p + 1);
        setIsFlipping(false);
      }, 400);
    }
  };
  const goPrev = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) triggerRipple(e);
    if (currentPage > 0 && !isFlipping) {
      setDirection('prev');
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentPage(p => p - 1);
        setIsFlipping(false);
      }, 400);
    }
  };
  const pageVariants = {
    enterNext: {
      opacity: 0,
      x: 120,
      rotateY: -18,
      scale: 0.92,
      filter: 'brightness(0.7)'
    },
    enterPrev: {
      opacity: 0,
      x: -120,
      rotateY: 18,
      scale: 0.92,
      filter: 'brightness(0.7)'
    },
    center: {
      opacity: 1,
      x: 0,
      rotateY: 0,
      scale: 1,
      filter: 'brightness(1)'
    },
    exitNext: {
      opacity: 0,
      x: -120,
      rotateY: 18,
      scale: 0.92,
      filter: 'brightness(0.7)'
    },
    exitPrev: {
      opacity: 0,
      x: 120,
      rotateY: -18,
      scale: 0.92,
      filter: 'brightness(0.7)'
    }
  };
  return <AnimatePresence data-id="element-105">
      {isOpen && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} onClick={onClose} style={{
      backgroundColor: 'rgba(0,0,0,0.92)',
      backdropFilter: 'blur(12px)'
    }} data-id="element-106">
          <motion.div className="relative w-full flex flex-col items-center" style={{
        maxWidth: '760px',
        maxHeight: '94vh'
      }} initial={{
        opacity: 0,
        y: 60,
        scale: 0.88,
        rotateX: 8
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1,
        rotateX: 0
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.94
      }} transition={{
        duration: 0.45,
        ease: [0.22, 1, 0.36, 1]
      }} onClick={e => e.stopPropagation()} data-id="element-107">
            {/* Glowing border effect */}
            <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
          boxShadow: '0 0 0 1px rgba(201,150,62,0.5), 0 0 40px rgba(201,150,62,0.15), 0 0 80px rgba(26,58,143,0.2)',
          borderRadius: '12px'
        }} data-id="element-108" />

            {/* Header bar */}
            <div className="w-full flex items-center justify-between px-5 py-3.5 rounded-t-xl" style={{
          background: 'linear-gradient(to right, #0a1628, #0d1f4e, #1a3a8f)',
          borderBottom: '1px solid rgba(201,150,62,0.4)'
        }} data-id="element-109">
              <div className="flex items-center gap-3" data-id="element-110">
                {/* Animated book icon */}
                <motion.div animate={{
              rotateY: [0, 15, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3
            }} data-id="element-111">
                  <BookOpenIcon size={18} style={{
                color: 'var(--gold)'
              }} data-id="element-112" />
                </motion.div>
                <span style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              fontWeight: 700,
              color: 'white',
              letterSpacing: '0.06em'
            }} data-id="element-113">
                  {title}
                </span>
              </div>
              <div className="flex items-center gap-4" data-id="element-114">
                {/* Page counter with animation */}
                <motion.span key={currentPage} initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              color: 'rgba(255,255,255,0.6)'
            }} data-id="element-115">
                  Page {currentPage + 1}{' '}
                  <span style={{
                color: 'var(--gold)'
              }} data-id="element-116">
                    / {pages.length}
                  </span>
                </motion.span>
                <button onClick={onClose} className="flex items-center justify-center w-7 h-7 rounded-full transition-all duration-200" style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'white',
              cursor: 'pointer'
            }} onMouseEnter={e => {
              ;
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,80,80,0.3)';
            }} onMouseLeave={e => {
              ;
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)';
            }} data-id="element-117">
                  <XIcon size={13} data-id="element-118" />
                </button>
              </div>
            </div>

            {/* Document viewer */}
            <div className="w-full relative flex items-center justify-center overflow-hidden" style={{
          background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
          minHeight: '520px',
          maxHeight: '68vh',
          perspective: '1400px'
        }} data-id="element-119">
              {/* Ambient glow behind page */}
              <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at center, rgba(26,58,143,0.15) 0%, transparent 70%)'
          }} data-id="element-120" />

              {/* Prev arrow */}
              <button onClick={e => goPrev(e)} disabled={currentPage === 0} className="absolute left-3 z-10 flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 overflow-hidden" style={{
            background: currentPage === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(201,150,62,0.2)',
            border: `1.5px solid ${currentPage === 0 ? 'rgba(255,255,255,0.08)' : 'rgba(201,150,62,0.6)'}`,
            color: currentPage === 0 ? 'rgba(255,255,255,0.2)' : 'var(--gold)',
            cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
            boxShadow: currentPage === 0 ? 'none' : '0 0 12px rgba(201,150,62,0.2)'
          }} data-id="element-121">
                <ChevronLeftIcon size={18} data-id="element-122" />
              </button>

              {/* Page image with enhanced flip animation */}
              <div className="relative flex items-center justify-center w-full h-full py-8 px-16" style={{
            perspective: '1400px'
          }} data-id="element-123">
                <AnimatePresence mode="wait" custom={direction} data-id="element-124">
                  <motion.div key={currentPage} custom={direction} variants={pageVariants} initial={direction === 'next' ? 'enterNext' : 'enterPrev'} animate="center" exit={direction === 'next' ? 'exitNext' : 'exitPrev'} transition={{
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} style={{
                transformStyle: 'preserve-3d',
                boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 4px 16px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
                borderRadius: '6px',
                overflow: 'hidden',
                maxHeight: '62vh',
                position: 'relative'
              }} data-id="element-125">
                    {/* Page shine overlay */}
                    <motion.div className="absolute inset-0 pointer-events-none z-10" initial={{
                  opacity: 0.4,
                  x: '-100%'
                }} animate={{
                  opacity: 0,
                  x: '200%'
                }} transition={{
                  duration: 0.6,
                  ease: 'easeOut'
                }} style={{
                  background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)'
                }} data-id="element-126" />
                    <img src={pages[currentPage]} alt={`${title} — Page ${currentPage + 1}`} style={{
                  display: 'block',
                  maxHeight: '62vh',
                  maxWidth: '100%',
                  width: 'auto',
                  objectFit: 'contain'
                }} data-id="element-127" />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Next arrow */}
              <button onClick={e => goNext(e)} disabled={currentPage === pages.length - 1} className="absolute right-3 z-10 flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 overflow-hidden" style={{
            background: currentPage === pages.length - 1 ? 'rgba(255,255,255,0.04)' : 'rgba(201,150,62,0.2)',
            border: `1.5px solid ${currentPage === pages.length - 1 ? 'rgba(255,255,255,0.08)' : 'rgba(201,150,62,0.6)'}`,
            color: currentPage === pages.length - 1 ? 'rgba(255,255,255,0.2)' : 'var(--gold)',
            cursor: currentPage === pages.length - 1 ? 'not-allowed' : 'pointer',
            boxShadow: currentPage === pages.length - 1 ? 'none' : '0 0 12px rgba(201,150,62,0.2)'
          }} data-id="element-128">
                <ChevronRightIcon size={18} data-id="element-129" />
              </button>
            </div>

            {/* Thumbnail strip + footer */}
            <div className="w-full px-5 py-3.5 flex items-center justify-between rounded-b-xl" style={{
          background: '#0d0d1a',
          borderTop: '1px solid rgba(201,150,62,0.2)'
        }} data-id="element-130">
              {/* Thumbnails */}
              <div className="flex items-center gap-2" data-id="element-131">
                {pages.map((page, i) => <motion.button key={i} onClick={() => {
              setDirection(i > currentPage ? 'next' : 'prev');
              setCurrentPage(i);
            }} whileHover={{
              scale: 1.08,
              y: -2
            }} whileTap={{
              scale: 0.95
            }} style={{
              width: '38px',
              height: '50px',
              borderRadius: '4px',
              overflow: 'hidden',
              border: i === currentPage ? '2px solid var(--gold)' : '2px solid rgba(255,255,255,0.1)',
              cursor: 'pointer',
              padding: 0,
              transition: 'border-color 0.2s',
              flexShrink: 0,
              boxShadow: i === currentPage ? '0 0 10px rgba(201,150,62,0.4)' : 'none'
            }} data-id="element-132">
                    <img src={page} alt={`Page ${i + 1}`} style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top'
              }} data-id="element-133" />
                  </motion.button>)}
              </div>

              {/* Animated dot indicators */}
              <div className="flex items-center gap-2" data-id="element-134">
                {pages.map((_, i) => <motion.button key={i} onClick={() => {
              setDirection(i > currentPage ? 'next' : 'prev');
              setCurrentPage(i);
            }} animate={{
              width: i === currentPage ? 24 : 7,
              backgroundColor: i === currentPage ? 'var(--gold)' : 'rgba(255,255,255,0.2)'
            }} transition={{
              duration: 0.3
            }} style={{
              height: '7px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              padding: 0
            }} data-id="element-135" />)}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}